(() => {
  if (customElements.get('bc-mobile-navigation')) return;
  class BCMobileNavigation extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.dialog = this.querySelector('dialog');
      this.mobile = matchMedia('(width < 768px)');
      this.applyNotes();
      document.addEventListener('shopify:section:load', () => this.applyNotes(), options);
      document.addEventListener('click', event => {
        const trigger = event.target.closest('[data-bc-mobile-trigger]');
        if (!trigger || trigger.getAttribute('aria-controls') !== this.dialog.id) return;
        event.preventDefault();
        this.dialog.open ? this.close() : this.open(trigger);
      }, options);
      this.addEventListener('click', event => {
        if (event.target.closest('[data-bc-mobile-close]')) this.close();
        const button = event.target.closest('[data-bc-mobile-expand]');
        if (button) this.expand(button, button.getAttribute('aria-expanded') !== 'true');
        if (event.target.closest('a')) this.close();
      }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      }, options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, options);
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const focusable = [...this.dialog.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')].filter(element => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length);
        const first = focusable[0];
        const last = focusable.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }, options);
      this.dialog.addEventListener('close', () => { if (!this.dialog.open) this.finishClose(); }, options);
      this.mobile.addEventListener('change', () => { if (!this.mobile.matches) this.close(); }, options);
    }
    applyNotes() {
      const records = [...document.querySelectorAll('[data-bc-mobile-notes]')].flatMap(element => {
        try { return JSON.parse(element.textContent); } catch { return []; }
      });
      const path = value => {
        try { return new URL(value, location.origin).pathname.replace(/\/$/, '').toLowerCase(); } catch { return ''; }
      };
      this.querySelectorAll('[data-bc-mobile-link]').forEach(link => {
        const directory = link.closest('.bc-mobile-navigation__children');
        const parentLink = directory?.previousElementSibling.querySelector('a');
        const title = link.dataset.bcMobileLink;
        const record = records.find(item => parentLink && path(item.parent) === path(parentLink.getAttribute('href')) && path(item.url) === path(link.getAttribute('href')) && (!item.title?.trim() || item.title.trim().toLowerCase() === title.toLowerCase()));
        const notes = record?.notes?.trim() || record?.fullNotes?.split(' · ').slice(0, 6).join(' · ').trim();
        link.querySelector('.bc-mobile-navigation__notes')?.remove();
        if (!notes) return;
        let name = link.querySelector('.bc-mobile-navigation__scent-name');
        if (!name) {
          name = document.createElement('span');
          name.className = 'bc-mobile-navigation__scent-name';
          name.textContent = title;
          link.replaceChildren(name);
        }
        const note = document.createElement('span');
        note.className = 'bc-mobile-navigation__notes';
        note.textContent = notes;
        link.append(note);
        link.classList.add('bc-mobile-navigation__scent-link');
        link.closest('li').classList.add('bc-mobile-navigation__scent-item');
        directory.classList.add('bc-mobile-navigation__scent-directory');
      });
    }
    disconnectedCallback() {
      this.close();
      this.controller?.abort();
    }
    expand(button, expanded) {
      const panel = this.querySelector('#' + CSS.escape(button.getAttribute('aria-controls')));
      const list = button.closest('.bc-mobile-navigation__list');
      const topLevel = list?.classList.contains('bc-mobile-navigation__list--1');
      const scroller = topLevel && expanded ? button.closest('.bc-mobile-navigation__body') : null;
      const buttonTop = scroller ? button.getBoundingClientRect().top : 0;
      if (topLevel && expanded) {
        list.querySelectorAll(':scope > li > .bc-mobile-navigation__row > [data-bc-mobile-expand]').forEach(peer => {
          if (peer === button || peer.getAttribute('aria-expanded') !== 'true') return;
          const peerPanel = this.querySelector('#' + CSS.escape(peer.getAttribute('aria-controls')));
          if (peerPanel.contains(document.activeElement)) button.focus({ preventScroll: true });
          this.expand(peer, false);
        });
      }
      button.setAttribute('aria-expanded', String(expanded));
      button.setAttribute('aria-label', (expanded ? 'Collapse ' : 'Expand ') + button.dataset.bcLabel + ' navigation');
      panel.hidden = !expanded;
      if (!expanded) panel.querySelectorAll('[data-bc-mobile-expand]').forEach(child => this.expand(child, false));
      if (scroller) scroller.scrollTop += button.getBoundingClientRect().top - buttonTop;
    }
    open(trigger) {
      if (!this.mobile.matches || this.dialog.open) return;
      this.trigger = trigger;
      this.scrollPosition = { x: window.scrollX, y: window.scrollY };
      this.savedStyles = {};
      ['position', 'top', 'left', 'width', 'overflow'].forEach(property => {
        this.savedStyles[property] = { value: document.body.style.getPropertyValue(property), priority: document.body.style.getPropertyPriority(property) };
      });
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + this.scrollPosition.y + 'px';
      document.body.style.left = '-' + this.scrollPosition.x + 'px';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      this.dialog.showModal();
      trigger.setAttribute('aria-expanded', 'true');
      this.querySelector('[data-bc-mobile-close]').focus({ preventScroll: true });
    }
    close() {
      if (!this.dialog?.open) return;
      this.dialog.close();
      this.finishClose();
    }
    finishClose() {
      if (!this.savedStyles) return;
      Object.entries(this.savedStyles).forEach(([property, original]) => {
        if (original.value) document.body.style.setProperty(property, original.value, original.priority);
        else document.body.style.removeProperty(property);
      });
      this.savedStyles = null;
      window.scrollTo(this.scrollPosition.x, this.scrollPosition.y);
      this.querySelectorAll('[data-bc-mobile-expand]').forEach(button => this.expand(button, false));
      this.trigger?.setAttribute('aria-expanded', 'false');
      if (this.mobile.matches && this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
    }
  }
  customElements.define('bc-mobile-navigation', BCMobileNavigation);
})();
